import dayjs from 'dayjs';
import { useState } from 'react';

import { BadgePadding } from 'types';
import { List } from 'components/list';
import { mapQuery } from 'utils/query';
import { formatDate } from 'utils/date';
import { ListCard } from 'components/list-card';
import { RefundDetails } from 'components/refunds';
import { ListHeader } from 'components/list-header';
import { isMobile, useSearchPagination } from 'hooks';
import { Refund, useListQuery } from 'store/api/refund';
import { FilterButton } from 'components/filter-button';
import { RefundStatusBadge } from 'components/refund-status-badge';
import {
  formatPrice,
  getCommissionsAmount,
  areSomeFiltersApplied,
} from 'utils/helpers';

import * as Styles from './styles';
import { Filters } from './filters';
import { getTotals } from './helpers';
import { initialValues } from './filters/config';
import { RefundsProps, FilterValues } from './types';

const template = '1fr 2fr repeat(6, 1fr)';
const cellsMobile = [
  { row: '2/3', col: '1/2' },
  { row: '1/2', col: '1/3' },
  { row: '2/3', col: '2/3' },
];
const titles = [
  'Date',
  'Event',
  'Sale Total',
  'Commissions',
  'Tip',
  'Refund Amount',
  'Refund Tip',
  'Status',
];

export const Refunds = ({ event }: RefundsProps) => {
  const mobile = isMobile();
  const pageData = useSearchPagination();
  const [isFilterOpened, setFilterOpened] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState<Refund>();
  const { currentPage: page, debounceQuery: search } = pageData;
  const [filters, setFilters] = useState<FilterValues>(initialValues);

  const { data, ...props } = useListQuery({
    page,
    search,
    ...filters,
    eventId: String(event.id),
    userIds: filters?.userIds?.length
      ? JSON.stringify(filters?.userIds)
      : undefined,
  });

  return (
    <>
      <List
        {...mapQuery(props)}
        {...pageData}
        count={data?.count}
        mainWrapperStyles={{ padding: 0 }}
        searchContainerStyle={{ margin: '16px 0' }}
        header={
          <ListHeader padding="8px" titles={titles} template={template} />
        }
        searchBarRightSlot={
          <FilterButton
            applied={areSomeFiltersApplied(filters)}
            onButtonClick={() => setFilterOpened(true)}
          />
        }
        topHeader={
          !props?.isLoading ? (
            <Styles.TotalContainer>
              {getTotals(data?.totals)?.map(({ title, value }) => (
                <Styles.StyledInfoCard
                  key={title}
                  title={title}
                  value={value}
                />
              ))}
            </Styles.TotalContainer>
          ) : undefined
        }
      >
        {data?.rows?.map((value) => {
          const timezoneDate = dayjs(value.createdAt).tz(
            value.checkout?.event?.timezone?.code
          );

          return (
            <ListCard
              key={value.id}
              headers={titles}
              template={template}
              cellsMobile={cellsMobile}
              onClick={() => setSelectedRefund(value)}
              values={[
                formatDate(timezoneDate),
                value.checkout?.event?.name,
                formatPrice(value.checkout?.saleTotal),
                formatPrice(getCommissionsAmount(value?.checkout)),
                formatPrice(value.checkout?.tip),
                formatPrice(value?.totalRefundPayments),
                formatPrice(value?.totalRefundTip),
                <RefundStatusBadge
                  status={value.status}
                  padding={BadgePadding.Default}
                />,
              ]}
            />
          );
        })}
      </List>
      <RefundDetails
        hideOverlay
        data={selectedRefund!}
        opened={!!selectedRefund}
        topOffset={mobile ? '10%' : '15%'}
        onClose={() => {
          setSelectedRefund(undefined);
        }}
      />
      <Filters
        values={filters}
        isOpen={isFilterOpened}
        setFilters={setFilters}
        setFilterOpened={setFilterOpened}
      />
    </>
  );
};
